import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { PremiumSponsorship } from "../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../services/premiumSponsorship/premiumSponsorshipService";
import { useConfirmationWithIntl } from "../../components/ConfirmationDialog";

const {
  acceptPremiumSponsorship,
  declinePremiumSponsorship,
  renouncePremiumSponsorship,
} = premiumSponsorshipService();

const PremiumSponsorshipSection = ({
  sponsorships,
  onChange,
}: {
  sponsorships: PremiumSponsorship[];
  onChange: () => void;
}) => {
  const { t } = useTranslation(["accounts"]);
  const { confirm } = useConfirmationWithIntl(["accounts"]);

  const pending = sponsorships.filter((s) => s.status === "invited");
  const active = sponsorships.filter(
    (s) => s.status === "accepted" && s.premiumGranted === true,
  );

  if (pending.length === 0 && active.length === 0) return null;

  return (
    <div>
      <h2 className="section_title">
        {t("accounts:premium-sponsorship.SECTION_TITLE")}
      </h2>
      <div className="lblock">
        {pending.map((sponsorship) => (
          <div
            key={`pending-${sponsorship.id}`}
            className="card --compact --border--light lblock"
          >
            <div className="card_body">
              <p>
                {t("accounts:premium-sponsorship.pending.DESCRIPTION", {
                  replace: {
                    organization: sponsorship.organization?.legalName,
                  },
                })}
              </p>
              <div className="cblock lrow --gap --txt--center">
                <button
                  className="btn--2"
                  onClick={() => {
                    void declinePremiumSponsorship(sponsorship.token!).then(
                      onChange,
                    );
                  }}
                >
                  {t("accounts:premium-sponsorship.pending.DECLINE_BUTTON")}
                </button>
                <button
                  className="btn--1"
                  onClick={() => {
                    void acceptPremiumSponsorship(sponsorship.token!).then(
                      () => {
                        window.location.reload();
                      },
                      (err) => {
                        // Session died before the click: `api.ts` has already
                        // logged us out, so reloading hands the candidate over
                        // to the private-route guard, i.e. the login page.
                        if (err?.response?.status === 401)
                          window.location.reload();
                      },
                    );
                  }}
                >
                  {t("accounts:premium-sponsorship.pending.ACCEPT_BUTTON")}
                </button>
              </div>
            </div>
          </div>
        ))}
        {active.map((sponsorship) => (
          <div
            key={`active-${sponsorship.id}`}
            className="card --compact --border--light lblock"
          >
            <div className="card_body">
              <p>
                {sponsorship.respondedAt
                  ? t(
                      "accounts:premium-sponsorship.active.SPONSORED_BY_SINCE",
                      {
                        replace: {
                          organization: sponsorship.organization?.legalName,
                          date: format(
                            new Date(sponsorship.respondedAt),
                            "dd/MM/yyyy",
                          ),
                        },
                      },
                    )
                  : t("accounts:premium-sponsorship.active.SPONSORED_BY", {
                      replace: {
                        organization: sponsorship.organization?.legalName,
                      },
                    })}
              </p>
              <p className="cblock">
                {t("accounts:premium-sponsorship.active.BENEFITS")}
              </p>
              <div className="cblock --txt--center">
                <button
                  className="btn--2"
                  onClick={() => {
                    confirm(
                      "accounts:premium-sponsorship.active.confirm-renounce.MESSAGE",
                      () =>
                        renouncePremiumSponsorship(sponsorship.id).then(
                          onChange,
                        ),
                      true,
                    );
                  }}
                >
                  {t("accounts:premium-sponsorship.active.RENOUNCE_BUTTON")}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PremiumSponsorshipSection;
